import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import { setBackButtonUrl } from "../uiSlice";
import { LOGIN_BACK_PATH } from "../gameSetupStorage";
import { normalizeThemeHexColor } from "../../functions/themeAssets";

const RULE_STYLES = [
  { icon: "fa-magnifying-glass", ring: "#7c3aed", card: "#efe9f6" },
  { icon: "fa-lightbulb", ring: "#f97316", card: "#fbe6d3" },
  { icon: "fa-star", ring: "#14b8a6", card: "#d3f4ef" },
  { icon: "fa-heart", ring: "#ec4899", card: "#fbe1ee" },
  { icon: "fa-handshake", ring: "#2563eb", card: "#dde7fb" },
  { icon: "fa-trophy", ring: "#eab308", card: "#faf1d1" },
];

export default function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  useEffect(() => {
    dispatch(setBackButtonUrl(LOGIN_BACK_PATH));
  }, [dispatch, status, user]);

  /* Unlock document scroll so logo + Back scroll away with the rules list.
   * Login CSS otherwise keeps body.common-bg overflow:hidden on mobile portrait. */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const snap = [];
    const setImp = (el, prop, value) => {
      if (!el) return;
      snap.push([
        el,
        prop,
        el.style.getPropertyValue(prop),
        el.style.getPropertyPriority(prop),
      ]);
      el.style.setProperty(prop, value, "important");
    };

    html.classList.add("sl-rules-open");
    body.classList.add("sl-rules-open");

    setImp(html, "height", "auto");
    setImp(html, "max-height", "none");
    setImp(html, "overflow-x", "hidden");
    setImp(html, "overflow-y", "auto");
    setImp(html, "overflow", "auto");

    setImp(body, "height", "auto");
    setImp(body, "min-height", "100%");
    setImp(body, "max-height", "none");
    setImp(body, "overflow", "visible");
    setImp(body, "overflow-x", "hidden");
    setImp(body, "overflow-y", "visible");
    setImp(body, "position", "static");
    setImp(body, "background-attachment", "scroll");
    setImp(body, "touch-action", "pan-y");

    setImp(root, "height", "auto");
    setImp(root, "min-height", "100%");
    setImp(root, "max-height", "none");
    setImp(root, "overflow", "visible");

    return () => {
      html.classList.remove("sl-rules-open");
      body.classList.remove("sl-rules-open");
      snap.forEach(([el, prop, value, priority]) => {
        if (value) el.style.setProperty(prop, value, priority || undefined);
        else el.style.removeProperty(prop);
      });
    };
  }, []);

  const rules = Array.isArray(themeData?.rules)
    ? themeData.rules.map((r) => String(r || "").trim()).filter(Boolean)
    : [];
  const ruleItems = rules.length ? rules : ["No rules defined"];

  const buttonColor = normalizeThemeHexColor(themeData?.questionBox_color) || "#7c3aed";
  const buttonTextColor = normalizeThemeHexColor(themeData?.button_Textcolor) || "#ffffff";

  if (themeStatus === "loading" && !themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div className="cr-rules-page">
      <div className="cr-rules-content">
        <div
          className="cr-rules-badge"
          style={{ background: buttonColor, color: buttonTextColor }}
        >
          HOW TO PLAY
        </div>

        <div className="cr-rules-cards">
          {ruleItems.map((rule, i) => {
            const styleSet = RULE_STYLES[i % RULE_STYLES.length];
            return (
              <div className="cr-rule-card" style={{ background: styleSet.card }} key={i}>
                <span className="cr-rule-icon" style={{ background: styleSet.ring }}>
                  <i className={`fa-solid ${styleSet.icon}`} aria-hidden="true" />
                </span>
                <p className="cr-rule-text">{rule}</p>
              </div>
            );
          })}
        </div>

        <div className="cr-rules-actions">
          <button
            type="button"
            className="cr-next-btn"
            style={{ background: buttonColor, color: buttonTextColor }}
            onClick={() => navigate("/game")}
          >
            NEXT
          </button>
          <button
            type="button"
            className="cr-next-circle"
            style={{ borderColor: buttonColor, color: buttonColor }}
            aria-label="Next"
            onClick={() => navigate("/game")}
          >
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="sl-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}
